import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Snackbar, Alert } from '@mui/material';

const SnackbarContext = createContext();

export const useSnackbar = () => {
  return useContext(SnackbarContext);
};

export const SnackbarProvider = ({ children }) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
    key: 0,
  });

  // `key` increments on every call and is passed to <Snackbar key=...> below —
  // without it, firing a second message while the first is still showing just
  // updates props on the same mounted Snackbar, so MUI's autoHideDuration timer
  // (already ticking down from the first message) doesn't restart, and the new
  // message can get cut off early. Remounting via a fresh key gives every
  // message its own full autoHideDuration.
  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar(prev => ({ open: true, message, severity, key: prev.key + 1 }));
  }, []);

  // UPDATED: Added event and reason parameters, and used functional state update
  const handleClose = useCallback((event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  const contextValue = useMemo(() => ({ showSnackbar }), [showSnackbar]);

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar
        key={snackbar.key}
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          zIndex: 2147483647,
        }}
      >
        <Alert
          onClose={handleClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};
